import { Box, FormMessage, Link, NumberField, Typography } from 'enova-frontend-components';
import { Control, Controller, useFormContext, useWatch } from 'react-hook-form';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { ByggType } from '../../../../../utils/registerEnums';
import { AboutFormData } from '../../types';
import useSelector from '../../../../../../../hooks/useSelector';

interface Props {
  control: Control<AboutFormData>;
}

export const FloorsField: FC<Props> = ({ control }) => {
  const { t } = useTranslation();
  const i18nPrefix = 'register.bygningsdetaljer.antallEtasjerUnntattKjeller';
  const { resetField } = useFormContext<AboutFormData>();
  const byggType = useWatch({ control, name: 'bygginfo.type' });
  const labelKey =
    byggType === ByggType.Leilighet
      ? 'register.bygningsdetaljer.leilighet.antallEtasjerUnntattKjeller.label'
      : `${i18nPrefix}.label`;
  const errorPrefix =
    byggType === ByggType.Leilighet ? 'register.bygningsdetaljer.leilighet.antallEtasjerUnntattKjeller' : i18nPrefix;

  // Changing floor count may affect other steps, so we need to check if etasjer
  // is defined in order to show a warning message
  const etasjer = useSelector((s) => s.register.userInput.bygningsdetaljer.etasjer);
  const hasEtasjerDefined = etasjer.length > 0;

  return (
    <Controller
      control={control}
      name="bygginfo.floors"
      rules={{
        validate: (value) => {
          if (!value || value < 1) {
            return t(`${errorPrefix}.error`);
          }
          // Allow maximum 10 floors
          if (value > 10) {
            return t(`${errorPrefix}.error_invalid`);
          }

          return true;
        }
      }}
      render={({ field, fieldState: { error, isDirty } }) => {
        return (
          <Box>
            <NumberField
              required
              integer
              error={!!error}
              helperText={error && t(`${error?.message}`)}
              value={field.value}
              useThousandSeparator={false}
              onValueChange={({ floatValue }) => {
                field.onChange(floatValue || null);
              }}
              label={t(labelKey)}
              HelperBoxProps={{
                'aria-label': t('readFieldInformation'),
                children: t(`${i18nPrefix}.information`)
              }}
            />
            {hasEtasjerDefined && isDirty && (
              <FormMessage title="" variant="warning" marginTop>
                <Typography>{t('register.bygningsdetaljer.antallEtasjerUnntattKjeller.warning')}</Typography>
                <Link button transparentBackground onClick={() => resetField('bygginfo.floors')}>
                  {t('reset')}
                </Link>
              </FormMessage>
            )}
          </Box>
        );
      }}
    />
  );
};
